import { useCallback, useMemo, useRef } from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormControl } from '@core/utils/form';
import { useObservable } from '@core/utils/hooks/rxjs';
import { LoadingState, useLoadingState } from '@core/utils/repository/loading_state';
import { FlashMessage } from '@core/components/FlashMessage';
import { CommonService } from '@business/services';
import { CompactLibraryItemResponse, ItemId, LibraryItemOptionResponse } from '@business/entities/library';
import { R } from '@core/utils/r';

export interface LibraryItemOptionsBloc {
  items$: BehaviorSubject<LibraryItemOptionResponse[] | null>;
  readonly loading: LoadingState;
  getContent(id: ItemId): Observable<CompactLibraryItemResponse>;
}

/**
 * Drives a library block's item dropdown: it lists names only, and the chosen item's content is
 * fetched on select and handed to `apply` to fill the block.
 */
export function useLibraryItemSelect(
  bloc: LibraryItemOptionsBloc,
  control: FormControl<string>,
  itemsLabel: string,
  apply: (item: CompactLibraryItemResponse, option: LibraryItemOptionResponse) => void,
) {
  const data = useObservable(bloc.items$);
  const loading = useLoadingState(bloc.loading);
  const items = useMemo(() => CommonService.selectItems(data || [], 'name', 'id'), [data]);
  // The item whose content is currently in the block, restored if loading a new one fails.
  const appliedId = useRef(control.value);

  const onChange = useCallback(
    (value: string) => {
      const option = R.find(data, o => o.id === value);
      if (!option) return;

      // Not tied to the settings panel's lifetime: the block still needs its content if the panel is closed meanwhile.
      bloc.getContent(option.id).subscribe({
        next: item => {
          if (control.value !== value) return; // a newer item was picked while this one loaded
          appliedId.current = value;
          apply(item, option);
        },
        error: e => {
          if (control.value !== value) return;
          control.setValue(appliedId.current);
          FlashMessage.error(e, `Could not load "${option.name}"`, 'Please try again.');
        },
      });
    },
    [apply, bloc, control, data],
  );

  const noOptionsMessage = loading.loading
    ? `Loading ${itemsLabel}...`
    : loading.error
    ? `Could not load ${itemsLabel}. Close and reopen this block to try again.`
    : undefined;

  return { items, onChange, noOptionsMessage };
}
